import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';
import { RESOURCES } from '../plans';
import { OnboardingStatusSchema } from './onboarding';

/**
 * GET /api/v1/home/summary — fixed Home dashboard for every plan (module onboarding-settings-home).
 * Read-only aggregation over reviews, deliveries, contacts and wallets, scoped to the tenant.
 * Truth rule: `googleClicks` counts clicks on the Google button, never posted reviews.
 */
export const HomeKpiSchema = z.object({
  /** Current calendar month in the tenant timezone. */
  value: z.number(),
  /** Same span of the previous month (day 1 → same day-of-month), for a fair comparison. */
  previous: z.number(),
});

export const HOME_ATTENTION_KINDS = [
  'negative_feedback',
  'failed_deliveries',
  'low_credits',
  'missing_google_link',
  /** Forms: pending CRM changes, unverified submissions, unlinked share answers, seat approvals. */
  'form_review',
  /** Forms: a reminder was skipped (needs confirmation, credits, downgrade). */
  'form_reminder_skipped',
  /** Forms: freed seats could not be offered (waitlisted people we may not message). */
  'form_waitlist_unreachable',
] as const;
export type HomeAttentionKind = (typeof HOME_ATTENTION_KINDS)[number];

export const HomeAttentionItemSchema = z.object({
  kind: z.enum(HOME_ATTENTION_KINDS),
  severity: z.enum(['high', 'medium', 'low']),
  count: z.number().int(),
  /** Resource for low_credits. */
  resource: z.enum(RESOURCES).optional(),
  /** Remaining units for low_credits. */
  available: z.number().int().optional(),
  /** Short excerpt of the newest item (negative feedback message), already truncated. */
  excerpt: z.string().nullable().optional(),
  contactName: z.string().nullable().optional(),
  at: IsoDateTimeSchema.nullable().optional(),
});
export type HomeAttentionItem = z.infer<typeof HomeAttentionItemSchema>;

export const HOME_ACTIVITY_KINDS = [
  'review_sent',
  'page_opened',
  'liked',
  'disliked',
  'google_clicked',
  'feedback_submitted',
  'opted_out',
  'contact_added',
  'delivery_failed',
  'form_response',
] as const;
export type HomeActivityKind = (typeof HOME_ACTIVITY_KINDS)[number];

export const HomeActivitySchema = z.object({
  id: z.string(),
  kind: z.enum(HOME_ACTIVITY_KINDS),
  at: IsoDateTimeSchema,
  contactId: UuidSchema.nullable(),
  contactName: z.string().nullable(),
  channel: z.string().nullable(),
});
export type HomeActivity = z.infer<typeof HomeActivitySchema>;

export const HomeSummarySchema = z.object({
  generatedAt: IsoDateTimeSchema,
  timezone: z.string(),
  period: z.object({ start: IsoDateTimeSchema, end: IsoDateTimeSchema }),
  business: z.object({ name: z.string(), planKey: z.string().nullable() }),
  kpis: z.object({
    reviewRequestsSent: HomeKpiSchema,
    /** Opened ÷ sent among this month's sent requests, 0–1 (null when nothing was sent). */
    openedRate: z.object({ value: z.number().nullable(), previous: z.number().nullable() }),
    googleClicks: HomeKpiSchema,
    privateFeedback: HomeKpiSchema,
  }),
  /** Daily review requests sent / Google clicks for the last 30 days (tenant-local dates). */
  daily: z.array(z.object({ date: z.string(), sent: z.number().int(), clicks: z.number().int() })),
  credits: z.array(
    z.object({
      resource: z.enum(RESOURCES),
      available: z.number().int(),
      allowance: z.number().int(),
      low: z.boolean(),
    }),
  ),
  contacts: z.object({ total: z.number().int(), addedThisMonth: z.number().int() }),
  attention: z.array(HomeAttentionItemSchema),
  activity: z.array(HomeActivitySchema),
  /** Forms engine tiles (only when the tenant has a form feature). */
  forms: z
    .object({
      open: z.number().int(),
      responses7d: z.number().int(),
      nextEvent: z
        .object({
          id: UuidSchema,
          title: z.string(),
          startsAt: IsoDateTimeSchema,
          going: z.number().int(),
          capacity: z.number().int().nullable(),
          /** Waitlisted seats (people + guests). */
          waitlist: z.number().int().default(0),
          checkinOpen: z.boolean().default(false),
        })
        .nullable(),
      /** Open forms and upcoming events, most relevant first (≤ 4). */
      active: z
        .array(
          z.object({
            id: UuidSchema,
            kind: z.enum(['interest', 'event']),
            title: z.string(),
            responses: z.number().int(),
            /** «Ενδιαφέρεται / Έρχεται» answers. */
            positive: z.number().int(),
            invited: z.number().int(),
            startsAt: IsoDateTimeSchema.nullable(),
            closesAt: IsoDateTimeSchema.nullable(),
          }),
        )
        .default([]),
    })
    .nullable()
    .optional(),
  onboarding: OnboardingStatusSchema.pick({
    complete: true,
    progress: true,
    currentStep: true,
    steps: true,
  }),
});
export type HomeSummary = z.infer<typeof HomeSummarySchema>;
